import React, { useEffect, useState } from 'react';
import {
  Card, TextField, ResourceList, ResourceItem, Text, Badge, EmptyState, BlockStack,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { api } from './apiClient';

/**
 * Search box + temporary results list. On click, calls onSelect and clears the
 * search. The persistent stack of selected SKUs lives in SkuStackPane.
 */
export default function SearchPane({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      setBusy(true);
      api.search(q).then(setResults).catch(() => setResults([])).finally(() => setBusy(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  function handleClick(hit) {
    onSelect(hit);
    setQ('');
    setResults([]);
  }

  const showResults = q.trim().length >= 2;

  return (
    <Card>
      <BlockStack gap="300">
        <TextField
          label=""
          labelHidden
          value={q}
          onChange={setQ}
          prefix={<SearchIcon />}
          placeholder="SKU, UPC, or description"
          autoComplete="off"
          autoFocus
          clearButton
          onClearButtonClick={() => setQ('')}
        />
        {showResults && (
          results.length === 0 && !busy ? (
            <EmptyState heading="No matches" image="">
              <p>Try a different search.</p>
            </EmptyState>
          ) : (
            <ResourceList
              resourceName={{ singular: 'SKU', plural: 'SKUs' }}
              loading={busy}
              items={results}
              renderItem={(item) => (
                <ResourceItem
                  id={`${item.article}|${item.color}`}
                  accessibilityLabel={item.description}
                  onClick={() => handleClick(item)}
                >
                  <BlockStack gap="100">
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      {item.description}
                    </Text>
                    <Text variant="bodySm" tone="subdued" as="p">
                      {item.article} · {item.color} · {item.business_segment}
                    </Text>
                    <Badge tone={item.removed ? 'critical' : undefined}>{item.map_tier}</Badge>
                  </BlockStack>
                </ResourceItem>
              )}
            />
          )
        )}
      </BlockStack>
    </Card>
  );
}
